import { CaretRightIcon, HouseIcon } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { Fragment, memo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Text } from '@components';
import { ApiEndpoint, FormType, Translation, VariantType } from '@enums';
import { useAppDispatch, useBreadcrumbItems, useFormContent, useWafSummary } from '@hooks';
import { useCdnSummary } from '@store';
import { cn } from '@utils';

const fadeInVariants = {
  initial: { opacity: 0 },
  animate: {
    opacity: 1,
    transition: {
      duration: 0.2,
      ease: 'easeOut',
    },
  },
};

const containerVariants = {
  animate: {
    transition: {
      staggerChildren: 0.03,
    },
  },
};

// Breadcrumb Item Component (Memoized for performance)
const BreadcrumbItemComponent = memo(
  ({
    item,
    index,
    isLastItem,
    handleClick,
  }: {
    item: {
      id: string;
      name: string;
      path: string;
    };
    index: number;
    isLastItem: boolean;
    handleClick: (path: string, index: number) => void;
  }) => {
    return (
      <motion.li
        className="flex items-center gap-x-3"
        data-testid={`breadcrumb-item-${index}`}
        variants={fadeInVariants}
        initial="initial"
        animate="animate"
      >
        <CaretRightIcon weight="bold" className="size-4 flex-shrink-0 text-gray-400" aria-hidden="true" />
        <motion.div whileHover={!isLastItem ? { opacity: 0.7 } : undefined} transition={{ duration: 0.1 }}>
          <Button
            variant={VariantType.Clean}
            onClick={() => handleClick(item.path, index)}
            disabled={isLastItem}
            className="text-sm bg-transparent enabled:shadow-none"
            aria-current={isLastItem ? 'page' : undefined}
            adornment={
              <Text
                $level={6}
                $customizeColor
                className={cn(isLastItem ? 'text-gray-400 text-opacity-40' : 'text-gray-600 hover:text-gray-500')}
              >
                {item.name}
              </Text>
            }
          />
        </motion.div>
      </motion.li>
    );
  },
);

BreadcrumbItemComponent.displayName = 'BreadcrumbItem';

export const NavigatorButton = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { pathname } = useLocation();
  const { t: translate } = useTranslation(Translation.Common);

  const cdnSummary = useCdnSummary();
  const wafSummary = useWafSummary();
  const workspace = useFormContent({ formType: FormType.Workspace, endpoint: ApiEndpoint.CONFIGURATION });

  const breadcrumbItems = useBreadcrumbItems(
    pathname,
    {
      cdnSource: cdnSummary,
      wafSource: wafSummary,
      workspaceSource: workspace,
    },
    translate,
  );

  const handleBreadCrumbItem = useCallback(
    (path: string, index: number): void => {
      navigate(path);
    },
    [navigate, pathname, dispatch],
  );

  const handleHomeClick = useCallback(() => {
    navigate('/');
  }, [navigate]);

  if (breadcrumbItems.length === 0) {
    return <Fragment />;
  }

  return (
    <motion.ol className="flex items-center space-x-3" variants={containerVariants} initial="initial" animate="animate">
      {/* Home icon */}
      <motion.li variants={fadeInVariants}>
        <motion.div whileHover={{ opacity: 0.7 }} transition={{ duration: 0.1 }}>
          <Button
            variant={VariantType.Clean}
            onClick={handleHomeClick}
            className="text-gray-400 hover:text-gray-500 enabled:shadow-none"
            aria-label="Home"
            adornment={<HouseIcon className="size-5" />}
          />
        </motion.div>
      </motion.li>

      {/* Breadcrumb items */}
      {breadcrumbItems.map((item, index) => (
        <BreadcrumbItemComponent
          key={`${item.path}-${index}`}
          item={item}
          index={index}
          isLastItem={index === breadcrumbItems.length - 1}
          handleClick={handleBreadCrumbItem}
        />
      ))}
    </motion.ol>
  );
};
